// It's capable to write iframe to a parent
// has a load event callback

import locationHelper from './location-helper';

export interface IFBIframeHelperOptions {
  // When action in iframe completes it sends a message though postMessage API,
  // to distinguish these messages (as there can be multiple iframes) we send state property in the announced event.
  // Optional expectedState restricts onMessage callback to be called only by events with a certain state value.
  expectedState?: string;

  // Optional, by default iframe is added style "display: none"
  hidden?: boolean; // default true

  // Optional callback for "load" event, receives event and iframe element
  onLoad?: (event: Event) => void;

  // Required onMessage callback, receives event with data property, see IFBIframeMessageEvent
  onMessage: (event: IFBIframeMessageEvent) => void;

  // Optional element to append iframe to, by default will be set to body
  parent?: HTMLElement;

  // Optional number of milliseconds to wait for iframe to call onMessage callback, default is 60s
  timeout?: number;

  // Optional callback to call if iframe did not announce onMessage event within specified timeout duration
  timeoutCallback?: () => void;

  // Required iframe src url
  url: string;
}

export interface IFBIframeMessageEvent {
  data: {
    // Required flag to sygnal that event is generated by our app, events without this property are ignored
    isFbMessage: boolean;
    // Iframe window location hash value
    hash?: string;
  };
}

export default class IframeHelper {
  private readonly expectedState: string;
  private readonly hidden: boolean;
  private readonly onLoad?: (event: Event) => void;
  private readonly onMessage: (event: IFBIframeMessageEvent) => void;
  private readonly parent: HTMLElement;
  private readonly timeout: number;
  private readonly timeoutCallback?: () => void;
  private readonly url: string;
  private readonly id: string = `iframe-${Math.random().toString(36).substr(2, 9)}`;
  private iframe: HTMLIFrameElement;
  private timeoutHandle: NodeJS.Timeout;
  private setInterval: NodeJS.Timeout;

  constructor(options: IFBIframeHelperOptions) {
    this.expectedState = options.expectedState || '';
    this.hidden = options.hidden !== false;
    this.onLoad = options.onLoad;
    this.onMessage = options.onMessage;
    this.parent = options.parent || window.document.body;
    this.timeout = options.timeout === 0 ? 0 : 60 * 1000;
    this.timeoutCallback = options.timeoutCallback;
    this.url = options.url;

    if (!this.url) {
      throw new Error('url is required.');
    }

    if (typeof this.onMessage !== 'function') {
      throw new Error('onMessage callback is required.');
    }
  }

  public init(): void {
    this.iframe = document.createElement('iframe');

    this.iframe.id = this.id;

    if (this.hidden) {
      this.iframe.style.display = 'none';
    }

    if (this.onLoad) {
      // Listen to iframe load event
      this.iframe.addEventListener('load', this.onLoad, false);
    }

    window.addEventListener('message', this.messageEventListener, false);
    this.parent.append(this.iframe);

    this.iframe.src = this.url;

    if (this.timeout) {
      this.timeoutHandle = setTimeout(this.timeoutHandler, this.timeout);
    }
  }

  private messageEventListener = (event: IFBIframeMessageEvent) => {
    // Skip events not generated by our application
    const isFbMessage = event && event.data && event.data.isFbMessage;
    if (!isFbMessage) {
      return;
    }

    const state = locationHelper.hashParam('state', event.data.hash as string);

    if (this.expectedState && state !== this.expectedState) {
      return;
    }

    this.destroy();
    this.onMessage(event);
  };

  private timeoutHandler = () => {
    this.destroy();
    if (this.timeoutCallback) {
      this.timeoutCallback();
    }
  };

  private destroy() {
    const instance = this;
    clearTimeout(this.timeoutHandle);

    if (this.setInterval) {
      clearInterval(this.setInterval);
    }

    setTimeout(() => {
      window.removeEventListener('message', instance.messageEventListener, false);

      // login-oauth-finish.html has own timeout closing it in 10 seconds (for the cases it's opened in a popup),
      // sometimes it's faster, hence the check is needed to get element by id
      if (instance.iframe && instance.parent && instance.id && document.getElementById(instance.id)) {
        instance.parent.removeChild(instance.iframe);
      }
    }, 0);
  }
}
